import { useEffect, useState } from "react";
import { toast } from "sonner";
import { icpAPI } from "../../lib/api";

const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";
const SHARED = "#A855F7";

const btn = (active = false) => ({
  background: active ? `${ACCENT}18` : "transparent",
  border: `1px solid ${active ? ACCENT : BORDER}`,
  color: active ? ACCENT : TEXT_MUTED,
  borderRadius: 7, padding: "7px 12px", fontSize: 12, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s", whiteSpace: "nowrap",
});

const ScopeTag = ({ scope }) => (
  <span style={{
    fontSize: 8.5, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em",
    padding: "2px 5px", borderRadius: 4, flexShrink: 0,
    background: scope === "workspace" ? `${SHARED}18` : "transparent",
    border: `1px solid ${scope === "workspace" ? `${SHARED}55` : BORDER}`,
    color: scope === "workspace" ? SHARED : TEXT_DIM,
  }}>
    {scope === "workspace" ? "SHARED" : "MINE"}
  </span>
);

/**
 * Named ICP presets — personal or shared with the workspace. The active
 * preset is a per-user choice and drives every new analysis's fit scoring.
 */
export const ICPPresetBar = ({ onSwitch }) => {
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'create' | 'rename' | null
  const [draft, setDraft] = useState("");
  const [draftShared, setDraftShared] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  const active = presets.find(p => p.is_active) || null;

  const load = async () => {
    try {
      const res = await icpAPI.presets();
      setPresets(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* surfaced on action */ }
  };

  useEffect(() => { load(); }, []);

  const handleActivate = async (target) => {
    if (!target || target.is_active) { setListOpen(false); return; }
    setBusy(true); setListOpen(false);
    try {
      const res = await icpAPI.activatePreset(target.preset_id);
      await load();
      if (onSwitch) await onSwitch();
      const stale = res.data?.newly_marked_stale || 0;
      toast.success(`Switched to “${target.name}”`, {
        description: stale > 0 ? `${stale} brief${stale === 1 ? "" : "s"} marked for re-scoring against this ICP.` : undefined,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch preset");
    }
    setBusy(false);
  };

  const submitDraft = async () => {
    if (busy) return;
    const name = draft.trim();
    if (!name) { setMode(null); return; }
    setBusy(true);
    try {
      if (mode === "create") {
        const res = await icpAPI.createPreset(name, true, draftShared ? "workspace" : "personal");
        await icpAPI.activatePreset(res.data.preset_id);
        await load();
        if (onSwitch) await onSwitch();
        toast.success(`Preset “${name}” created`, {
          description: draftShared
            ? "Shared with your workspace — teammates can switch to it."
            : "Copied from your current ICP. Only you can see it.",
        });
      } else if (mode === "rename" && active) {
        await icpAPI.renamePreset(active.preset_id, name);
        await load();
        toast.success("Preset renamed");
      }
      setMode(null); setDraft(""); setDraftShared(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save preset");
    }
    setBusy(false);
  };

  const handleShare = async () => {
    if (!active || busy) return;
    setBusy(true);
    try {
      await icpAPI.sharePreset(active.preset_id);
      await load();
      toast.success(`“${active.name}” is now shared with your workspace`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not share preset");
    }
    setBusy(false);
  };

  const handleDelete = async () => {
    if (!active || busy) return;
    setBusy(true);
    try {
      await icpAPI.deletePreset(active.preset_id);
      await load();
      if (onSwitch) await onSwitch();
      toast.success(`Preset “${active.name}” deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete preset");
    }
    setBusy(false);
  };

  const canEditActive = !!active && active.can_edit !== false;

  return (
    <div data-testid="icp-preset-bar" style={{
      background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flex: 1, position: "relative" }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 6 }}>
            ACTIVE PRESET
          </div>
          <button type="button" data-testid="icp-preset-select" onClick={() => setListOpen(o => !o)} style={{
            width: "100%", background: "#06080F", border: `1px solid ${listOpen ? ACCENT : BORDER}`,
            borderRadius: 8, padding: "10px 14px", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "space-between", gap: 10, fontFamily: "'DM Sans', sans-serif",
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 13, color: active ? TEXT_PRIMARY : TEXT_MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {active?.name || (presets.length ? "Select a preset" : "Loading…")}
              </span>
              {active && <ScopeTag scope={active.scope} />}
            </span>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}
              style={{ transform: listOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {listOpen && (
            <div data-testid="icp-preset-list" style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 60,
              background: "#0A0D13", border: `1px solid ${BORDER}`, borderRadius: 10,
              maxHeight: 280, overflowY: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.6)", padding: "4px 0",
            }}>
              {presets.map(p => (
                <button key={p.preset_id} type="button"
                  data-testid={`icp-preset-option-${p.preset_id}`}
                  onClick={() => handleActivate(p)}
                  onMouseEnter={e => e.currentTarget.style.background = SURFACE_2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    width: "100%", background: "none", border: "none", padding: "9px 14px",
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left",
                  }}>
                  <span style={{ fontSize: 13, color: p.is_active ? ACCENT : TEXT_PRIMARY, fontWeight: p.is_active ? 600 : 400 }}>
                    {p.name}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                      {p.industries_count}I · {p.countries_count}C
                    </span>
                    <ScopeTag scope={p.scope} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <button data-testid="icp-preset-new-btn" disabled={busy}
            onClick={() => { setMode("create"); setDraft(""); setDraftShared(false); }} style={btn(mode === "create")}>
            + New preset
          </button>
          {active?.scope !== "workspace" && (
            <button data-testid="icp-preset-share-btn" disabled={busy || !active}
              onClick={handleShare} style={{ ...btn(), color: SHARED, borderColor: `${SHARED}55` }}>
              Share with team
            </button>
          )}
          <button data-testid="icp-preset-rename-btn" disabled={busy || !canEditActive}
            onClick={() => { setMode("rename"); setDraft(active?.name || ""); }}
            style={{ ...btn(mode === "rename"), opacity: canEditActive ? 1 : 0.4 }}>
            Rename
          </button>
          <button data-testid="icp-preset-delete-btn" disabled={busy || presets.length <= 1 || !canEditActive}
            onClick={handleDelete}
            style={{
              ...btn(),
              color: (presets.length <= 1 || !canEditActive) ? TEXT_DIM : "#EF4444",
              borderColor: (presets.length <= 1 || !canEditActive) ? BORDER : "#EF444455",
              opacity: (presets.length <= 1 || !canEditActive) ? 0.5 : 1,
            }}>
            Delete
          </button>
        </div>
      </div>

      {mode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              data-testid="icp-preset-name-input"
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitDraft(); if (e.key === "Escape") { setMode(null); setDraft(""); } }}
              placeholder={mode === "create" ? "e.g. EU Mid-Market, Shopify Apps, US Enterprise" : "New name"}
              style={{
                flex: 1, background: "#06080F", border: `1px solid ${BORDER}`, borderRadius: 7,
                padding: "9px 12px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
            <button data-testid="icp-preset-save-name-btn" onClick={submitDraft} disabled={busy}
              style={{ ...btn(true), background: ACCENT, color: "#fff", borderColor: ACCENT, fontWeight: 600 }}>
              {mode === "create" ? "Create" : "Save"}
            </button>
            <button data-testid="icp-preset-cancel-btn" onClick={() => { setMode(null); setDraft(""); }} style={btn()}>
              Cancel
            </button>
          </div>
          {mode === "create" && (
            <label data-testid="icp-preset-shared-toggle" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: TEXT_MUTED, cursor: "pointer" }}>
              <input type="checkbox" checked={draftShared} onChange={e => setDraftShared(e.target.checked)}
                style={{ accentColor: SHARED, width: 14, height: 14, cursor: "pointer" }} />
              Share with my workspace — teammates can switch to it (owners and admins only)
            </label>
          )}
        </div>
      )}

      <p style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.55 }}>
        {active
          ? `Every new analysis scores against “${active.name}”. ${active.scope === "workspace" ? "This is a shared preset — switching is personal to you, but edits affect the whole workspace." : "New presets start as a copy of the preset you're on, so you only change what differs."}`
          : "Presets let you keep separate ICPs for different products or territories."}
        {presets.length > 1 && (
          <span data-testid="icp-preset-count" style={{ color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}> · {presets.length} PRESETS</span>
        )}
      </p>
    </div>
  );
};
